import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Check, Loader2, MapPin, Mail, Phone } from 'lucide-react';
import axios from 'axios';
import { ServiceAreaMap } from '../components/ServiceAreaMap';
import { useSiteContent } from '../context/SiteContentContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const { c } = useSiteContent();
  const [formData, setFormData] = useState({ full_name: '', email: '', phone: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const update = (key, val) => setFormData(prev => ({ ...prev, [key]: val }));

  return (
    <div className="min-h-screen pt-24" data-testid="contact-page">
      {/* Hero */}
      <section className="py-20 border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-manrope font-semibold mb-4">Get In Touch</p>
            <h1 className="font-playfair text-4xl lg:text-5xl text-white mb-4">{c('contact.title', 'Contact Us')}</h1>
            <p className="text-white/40 font-manrope text-lg max-w-2xl leading-relaxed">
              {c('contact.subtitle', "Have a question about our services? Ready to start your dream theater project? We'd love to hear from you.")}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-8">
              <div className="bg-[#111113] border border-white/5 rounded-sm p-8">
                <h3 className="font-playfair text-lg text-white mb-6">Contact Information</h3>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-sm bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                      <MapPin size={16} className="text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className="text-white/80 font-manrope text-sm font-semibold">Location</p>
                      <p className="text-white/40 font-manrope text-sm">Greater Toronto Area, Ontario, Canada</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-sm bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                      <Mail size={16} className="text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className="text-white/80 font-manrope text-sm font-semibold">Email</p>
                      <a href="mailto:info@dreamroomtheaters.com" className="text-[#D4AF37] font-manrope text-sm hover:underline">
                        info@dreamroomtheaters.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#111113] border border-white/5 rounded-sm p-8">
                <h3 className="font-playfair text-lg text-white mb-4">Business Hours</h3>
                <div className="space-y-2 text-sm font-manrope">
                  <div className="flex justify-between">
                    <span className="text-white/40">Monday - Friday</span>
                    <span className="text-white/70">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/40">Saturday</span>
                    <span className="text-white/70">10:00 AM - 4:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/40">Sunday</span>
                    <span className="text-white/70">Closed</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              {submitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-[#D4AF37]/5 border border-[#D4AF37] rounded-sm p-12 text-center"
                  data-testid="contact-success"
                >
                  <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-6">
                    <Check size={32} className="text-black" />
                  </div>
                  <h2 className="font-playfair text-2xl text-white mb-3">Message Sent!</h2>
                  <p className="text-white/50 font-manrope text-sm">
                    Thank you for reaching out. We'll get back to you within 24 hours.
                  </p>
                </motion.div>
              ) : (
                <motion.form
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  onSubmit={handleSubmit}
                  className="bg-[#111113] border border-white/5 rounded-sm p-8 lg:p-10 space-y-6"
                  data-testid="contact-form"
                >
                  <h3 className="font-playfair text-xl text-white mb-2">Send Us a Message</h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-manrope mb-2 block">Full Name</label>
                      <input
                        type="text" required
                        value={formData.full_name}
                        onChange={e => update('full_name', e.target.value)}
                        className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-3 text-white placeholder:text-white/20 font-manrope transition-colors"
                        placeholder="John Smith"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-manrope mb-2 block">Email</label>
                      <input
                        type="email" required
                        value={formData.email}
                        onChange={e => update('email', e.target.value)}
                        className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-3 text-white placeholder:text-white/20 font-manrope transition-colors"
                        placeholder="john@example.com"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-manrope mb-2 block">Phone</label>
                    <input
                      type="tel" required
                      value={formData.phone}
                      onChange={e => update('phone', e.target.value)}
                      className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-3 text-white placeholder:text-white/20 font-manrope transition-colors"
                      placeholder="(416) 555-0123"
                      data-testid="contact-phone-input"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-manrope mb-2 block">Message</label>
                    <textarea
                      required
                      value={formData.message}
                      onChange={e => update('message', e.target.value)}
                      rows={5}
                      className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-3 text-white placeholder:text-white/20 font-manrope transition-colors resize-none"
                      placeholder="Tell us about your project..."
                      data-testid="contact-message-input"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-3 bg-[#D4AF37] text-black hover:bg-[#C5A059] uppercase tracking-widest px-8 py-4 rounded-sm font-manrope font-bold text-sm transition-all duration-300 disabled:opacity-50"
                    data-testid="submit-contact-btn"
                  >
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <>
                      <Send size={16} /> Send Message
                    </>}
                  </button>
                </motion.form>
              )}
            </div>
          </div>

          {/* Service Area Map */}
          <div className="mt-16" data-testid="service-area-section">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-manrope font-semibold mb-3">Where We Work</p>
            <h3 className="font-playfair text-2xl text-white mb-2">Our Service Area</h3>
            <p className="text-white/40 font-manrope text-sm mb-8 max-w-xl">
              Proudly serving the Greater Toronto Area and surrounding regions across Ontario.
            </p>
            <ServiceAreaMap height={420} />
          </div>
        </div>
      </section>
    </div>
  );
}
